import React, { useMemo } from 'react';
import { ChevronLeft, ChevronRight, Users, ShieldCheck, RefreshCw } from 'lucide-react';

const LEAVE_TYPE_LABELS = {
  paid_leave: 'Congé payé (CP)',
  sick_leave: 'Arrêt maladie',
  work_accident: 'Accident du travail (AT)',
  compensatory: 'Repos compensateur (HS)',
  unjustified: 'Absence injustifiée',
};

const LEAVE_TYPE_SHORT_LABELS = {
  paid_leave: 'CP',
  sick_leave: 'Maladie',
  work_accident: 'AT',
  compensatory: 'HS',
  unjustified: 'Absence',
};

const LEAVE_TYPE_STYLES = {
  paid_leave: {
    badge: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    dot: 'bg-emerald-500',
    border: 'border-emerald-200',
  },
  sick_leave: {
    badge: 'bg-orange-50 text-orange-700 border-orange-200',
    dot: 'bg-orange-500',
    border: 'border-orange-200',
  },
  work_accident: {
    badge: 'bg-red-50 text-red-700 border-red-200',
    dot: 'bg-red-500',
    border: 'border-red-200',
  },
  compensatory: {
    badge: 'bg-purple-50 text-purple-700 border-purple-200',
    dot: 'bg-purple-500',
    border: 'border-purple-200',
  },
  unjustified: {
    badge: 'bg-slate-100 text-slate-700 border-slate-300',
    dot: 'bg-slate-600',
    border: 'border-slate-300',
  },
};

export default function AnonymousTeamLeaveCalendar({
  calendarData,
  currentYear,
  currentMonth,
  onPrevMonth,
  onNextMonth,
  onToday,
  loading = false,
}) {
  const getDaysInMonth = (year, month) => {
    const date = new Date(year, month - 1, 1);
    const result = [];
    const firstDayIndex = (date.getDay() + 6) % 7; // Monday = 0
    for (let i = 0; i < firstDayIndex; i++) {
      result.push(null);
    }
    while (date.getMonth() === month - 1) {
      result.push(new Date(date));
      date.setDate(date.getDate() + 1);
    }
    return result;
  };

  const getMonthNames = () => [
    'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
    'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
  ];

  const formatDateStr = (d) => {
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  };

  const todayStr = useMemo(() => formatDateStr(new Date()), []);

  const absencesMap = useMemo(() => {
    const map = {};
    if (calendarData && Array.isArray(calendarData.leaves)) {
      calendarData.leaves.forEach(leave => {
        if (leave.status === 'approved') {
          const start = leave.start_datetime ? leave.start_datetime.slice(0, 10) : leave.start_date;
          const end = leave.end_datetime ? leave.end_datetime.slice(0, 10) : leave.end_date;

          let current = new Date(`${start}T00:00:00Z`);
          const endDate = new Date(`${end}T00:00:00Z`);
          const isReturnConvention = leave.end_datetime && /T00:00:00/.test(leave.end_datetime);

          if (isReturnConvention) {
            endDate.setDate(endDate.getDate() - 1);
          }

          while (current <= endDate) {
            const dStr = formatDateStr(current);
            if (!map[dStr]) map[dStr] = [];
            map[dStr].push({
              leave_type: leave.leave_type || 'paid_leave',
            });
            current.setDate(current.getDate() + 1);
          }
        }
      });
    }
    return map;
  }, [calendarData]);

  const days = getDaysInMonth(currentYear, currentMonth);
  const monthName = getMonthNames()[currentMonth - 1];

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-6 border-b border-gray-100 bg-gray-50/50 gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h3 className="text-xl font-bold text-gray-800 flex items-center gap-2">
              <Users className="w-5 h-5 text-[#6E56CF]" />
              Absences de l'équipe
            </h3>
            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-indigo-50 text-indigo-700 border border-indigo-200">
              <ShieldCheck className="w-3.5 h-3.5 text-indigo-600" />
              Anonymisé
            </span>
          </div>
          <p className="text-xs sm:text-sm text-gray-500 mt-1">
            Consultez les congés posés par l'équipe pour mieux planifier vos demandes
          </p>
        </div>

        <div className="flex items-center gap-3 w-full sm:w-auto justify-between sm:justify-end">
          <div className="text-base sm:text-lg font-bold text-[#1A1B4B] min-w-[130px] text-center sm:text-right">
            {monthName} {currentYear}
          </div>
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={onPrevMonth}
              className="p-2 bg-white border border-gray-200 rounded-xl hover:bg-gray-100 text-gray-600 shadow-sm transition-colors"
              title="Mois précédent"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            {onToday && (
              <button
                type="button"
                onClick={onToday}
                className="px-3 py-1.5 bg-white border border-gray-200 rounded-xl text-xs font-semibold hover:bg-gray-100 text-gray-700 shadow-sm transition-colors"
                title="Revenir à ce mois-ci"
              >
                Aujourd'hui
              </button>
            )}
            <button
              type="button"
              onClick={onNextMonth}
              className="p-2 bg-white border border-gray-200 rounded-xl hover:bg-gray-100 text-gray-600 shadow-sm transition-colors"
              title="Mois suivant"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Calendar Grid */}
      <div className="p-4 sm:p-6 relative">
        {loading && (
          <div className="absolute inset-0 bg-white/70 backdrop-blur-[1px] flex items-center justify-center z-10">
            <RefreshCw className="w-6 h-6 animate-spin text-[#6E56CF]" />
          </div>
        )}

        <div className="overflow-x-auto custom-scrollbar pb-2">
          <div className="grid grid-cols-7 gap-px bg-gray-200 border border-gray-200 rounded-xl overflow-hidden min-w-[320px]">
            {['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'].map((d, i) => (
              <div key={i} className="bg-gray-50 py-2.5 text-center text-xs font-bold text-gray-500 uppercase tracking-wider">
                <span className="sm:hidden">{d.charAt(0)}</span>
                <span className="hidden sm:inline">{d}</span>
              </div>
            ))}

            {days.map((date, idx) => {
              if (!date) {
                return <div key={`empty-${idx}`} className="bg-gray-50/40 min-h-[70px] sm:min-h-[90px]" />;
              }

              const dateStr = formatDateStr(date);
              const isWeekend = date.getDay() === 0 || date.getDay() === 6;
              const isToday = dateStr === todayStr;
              const dayAbsences = absencesMap[dateStr] || [];
              const totalCount = dayAbsences.length;

              // Group by leave type
              const countsByType = dayAbsences.reduce((acc, item) => {
                const t = item.leave_type || 'paid_leave';
                acc[t] = (acc[t] || 0) + 1;
                return acc;
              }, {});

              const tooltipParts = Object.entries(countsByType).map(
                ([type, count]) => `${count} ${LEAVE_TYPE_LABELS[type] || 'Congé'}`
              );
              const tooltipText = totalCount > 0
                ? `${totalCount} personne${totalCount > 1 ? 's' : ''} absente${totalCount > 1 ? 's' : ''} (${tooltipParts.join(', ')})`
                : '';

              return (
                <div
                  key={dateStr}
                  title={tooltipText}
                  className={`bg-white min-h-[70px] sm:min-h-[90px] p-1.5 sm:p-2 flex flex-col transition-colors hover:bg-gray-50/80 ${
                    isWeekend ? 'bg-gray-50/50' : ''
                  } ${isToday ? 'ring-2 ring-inset ring-[#6E56CF]/30 bg-purple-50/20' : ''}`}
                >
                  {/* Day number */}
                  <div className="flex items-center justify-between mb-1">
                    <span
                      className={`text-xs sm:text-sm font-bold inline-flex items-center justify-center ${
                        isToday
                          ? 'w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-[#1A1B4B] text-white text-[11px] sm:text-xs'
                          : isWeekend
                          ? 'text-gray-400'
                          : 'text-gray-700'
                      }`}
                    >
                      {date.getDate()}
                    </span>

                    {totalCount > 0 && (
                      <span className="hidden sm:inline-flex items-center text-[10px] font-bold text-gray-400">
                        {totalCount} {totalCount > 1 ? 'absents' : 'absent'}
                      </span>
                    )}
                  </div>

                  {/* Absences badges */}
                  <div className="flex flex-col gap-1 flex-1 justify-start">
                    {totalCount === 0 ? null : (
                      <>
                        {Object.entries(countsByType).map(([type, count]) => {
                          const style = LEAVE_TYPE_STYLES[type] || LEAVE_TYPE_STYLES.paid_leave;
                          const label = LEAVE_TYPE_SHORT_LABELS[type] || 'Congé';
                          return (
                            <div
                              key={type}
                              className={`flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] sm:text-[11px] font-semibold border shadow-xs ${style.badge}`}
                            >
                              <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${style.dot}`} />
                              <span className="truncate">
                                {count > 1 ? `${count} × ${label}` : label}
                              </span>
                            </div>
                          );
                        })}
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Legend and privacy note */}
        <div className="mt-4 pt-4 border-t border-gray-100 flex flex-col md:flex-row items-start md:items-center justify-between gap-3 text-xs text-gray-600">
          <div className="flex flex-wrap items-center gap-3">
            <span className="flex items-center gap-1.5 font-medium">
              <span className="w-2.5 h-2.5 rounded-full bg-emerald-500" /> Congés payés
            </span>
            <span className="flex items-center gap-1.5 font-medium">
              <span className="w-2.5 h-2.5 rounded-full bg-orange-500" /> Maladie
            </span>
            <span className="flex items-center gap-1.5 font-medium">
              <span className="w-2.5 h-2.5 rounded-full bg-red-500" /> Accident travail
            </span>
            <span className="flex items-center gap-1.5 font-medium">
              <span className="w-2.5 h-2.5 rounded-full bg-purple-500" /> Repos comp. (HS)
            </span>
            <span className="flex items-center gap-1.5 font-medium">
              <span className="w-2.5 h-2.5 rounded-full bg-slate-600" /> Absence injustifiée
            </span>
          </div>
          <div className="text-[11px] text-gray-400 flex items-center gap-1 italic">
            <ShieldCheck className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
            Noms masqués pour garantir la confidentialité
          </div>
        </div>
      </div>
    </div>
  );
}
